import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY, estiloH3NY, estiloPNY } from "./nyHelpers";

// Template73: Día 1 Midtown 2 - Bryant Park, Summit, Times Square. Contenido desde page.texts, page.images, page.maps (servidor).
const Template73 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItems = (html) => {
		if (!html) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const ps = doc.querySelectorAll("p");
		const items = [];
		ps.forEach((p) => {
			const text = (p.textContent ?? p.innerText ?? "").trim();
			if (!text) return;
			const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
			if (emojiMatch)
				items.push({ icono: emojiMatch[1], texto: emojiMatch[2].trim() });
		});
		return items;
	};

	const title = page?.texts?.[0]?.content;
	const bryantTitle = page?.texts?.[2]?.content;
	const bryantBodyHtml = page?.texts?.[4]?.content;
	const summitTitle = page?.texts?.[5]?.content;
	const summitBodyHtml = page?.texts?.[6]?.content;
	const timesTitle = page?.texts?.[7]?.content;
	const timesBodyHtml = page?.texts?.[8]?.content;
	const sabiasQueItems = parseSabiasQueItems(page?.texts?.[9]?.content);
	const mapYellowBoxHtml = page?.texts?.[10]?.content;
	const chryslerStickerText = page?.texts?.[11]?.content;
	const broadwayBoxTitle = page?.texts?.[12]?.content;
	const broadwayBoxBodyHtml = page?.texts?.[13]?.content;

	const bryantSrc = getImageSrc("bryant");
	const summitSrc = getImageSrc("summit");
	const times2Src = getImageSrc("times2");
	const broadwaySrc = getImageSrc("broadway");
	const summitStickerSrc = getImageSrc("summit-sticker");
	const midtownStickerSrc = getImageSrc("midtown-sticker");
	const logoSrc = getCanvasImageUrl("logo");

	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const mapUrl = page?.maps?.[0];

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#59c3d8",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#59c3d8",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					sx={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
				"& .margin": {
					marginBottom: "8px !important",
				},
				"& a": {
					color: "#007a8d",
					fontWeight: 700,
					textDecoration: "underline",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 10539,
						}}
					/>
				)}
			</div>

			{mapUrl && (
				<div data-block-id="map" style={{ flex: 1, marginTop: "-70px", position: "relative" }}>
					{!loaded && isOnline && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: isSmallScreen ? 400 : 330,
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								zIndex: 1,
							}}
						>
							<div
								style={{
									width: 20,
									height: 20,
									border: "4px solid #ccc",
									borderTop: "4px solid #59c3d8",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
								}}
							/>
						</div>
					)}
					{isOnline && (
						<Box
							component="button"
							onClick={() => {
								setFullscreen(true);
								onMapFullscreenChange?.(true);
							}}
							sx={{
								position: "absolute",
								top: 80,
								right: 10,
								backgroundColor: "#59c3d8",
								color: "#fff",
								padding: "8px 10px",
								fontSize: "11px",
								borderRadius: "8px",
								boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

								border: "none",
								cursor: "pointer",
								fontFamily: fontFamily || '"Poppins", sans-serif',
							}}
						>
							Ver mapa grande
						</Box>
					)}
					{isOnline && (
						<Box
							component="iframe"
							title="Mapa midtown"
							src={mapUrl}
							width="100%"
							height={isSmallScreen ? 400 : 330}
							sx={{ border: 0, position: "relative", zIndex: 2 }}
							loading="lazy"
							onLoad={() => setLoaded(true)}
						/>
					)}
					{mapYellowBoxHtml && (
						<div
							style={{
								position: "absolute",
								bottom: 80,
								right: 5,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: "'Poppins', sans-serif",
								color: "#444",
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 120,
								zIndex: 3,
							}}
							dangerouslySetInnerHTML={{ __html: mapYellowBoxHtml }}
						/>
					)}
					{summitStickerSrc && (
						<img
							src={summitStickerSrc}
							alt="Summit"
							style={{
								width: 110,
								position: "absolute",
								bottom: 0,
								right: -10,
								zIndex: 2,
							}}
						/>
					)}
				</div>
			)}

			<div style={{ padding: "10px 12px 0px", position: "relative" }}>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{(bryantTitle || bryantBodyHtml || bryantSrc) && (
						<div data-block-id="text-1" style={{ display: "flex", gap: 16, position: "relative" }}>
							{bryantSrc && (
								<img
									src={bryantSrc}
									alt="Bryant Park"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							<div style={{ width: "60%" }}>
								{bryantTitle && <h3 style={estiloH3NY}>{bryantTitle}</h3>}
								{bryantBodyHtml && (
									<div
										style={estiloPNY}
										dangerouslySetInnerHTML={{ __html: bryantBodyHtml }}
									/>
								)}
							</div>
						</div>
					)}
					{chryslerStickerText && (
						<div
							style={{
								position: "absolute",
								top: isSmallScreen ? 90 : 80,
								left: isSmallScreen ? 0 : 50,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 16px 0px",
								padding: "10px 12px",
								fontSize: 11,
								fontFamily: "'Poppins', sans-serif",
								color: "#444",
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 100,
								zIndex: 3,
							}}
							dangerouslySetInnerHTML={{ __html: chryslerStickerText }}
						/>
					)}
					{midtownStickerSrc && (
						<img
							src={midtownStickerSrc}
							alt="Building"
							style={{
								width: 110,
								position: "absolute",
								top: isSmallScreen ? 140 : 120,
								left: -20,
								zIndex: 2,
							}}
						/>
					)}

					{(summitTitle || summitBodyHtml || summitSrc) && (
						<div data-block-id="text-2" style={{ display: "flex", gap: 16, position: "relative" }}>
							{summitSrc && (
								<img
									src={summitSrc}
									alt="Summit"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							{sabiasQueItems[0] && (
								<div style={{ position: "absolute", top: 40, left: "33%" }}>
									<SabiasQueNY
										icono={sabiasQueItems[0].icono}
										texto={sabiasQueItems[0].texto}
									/>
								</div>
							)}
							{sabiasQueItems[1] && (
								<div style={{ position: "absolute", top: -10, left: "33%" }}>
									<SabiasQueNY
										icono={sabiasQueItems[1].icono}
										texto={sabiasQueItems[1].texto}
									/>
								</div>
							)}
							<div style={{ width: "60%" }}>
								{summitTitle && <h3 style={estiloH3NY}>{summitTitle}</h3>}
								{summitBodyHtml && (
									<div
										style={estiloPNY}
										dangerouslySetInnerHTML={{ __html: summitBodyHtml }}
									/>
								)}
							</div>
						</div>
					)}

					{(timesTitle || timesBodyHtml || times2Src) && (
						<div data-block-id="text-3" style={{ display: "flex", gap: 16, position: "relative" }}>
							{times2Src && (
								<img
									src={times2Src}
									alt="Times Square"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: 12,
									}}
								/>
							)}
							{sabiasQueItems[2] && (
								<div style={{ position: "absolute", top: 50, left: "33%" }}>
									<SabiasQueNY
										icono={sabiasQueItems[2].icono}
										texto={sabiasQueItems[2].texto}
									/>
								</div>
							)}
							<div
								style={{
									width: "60%",
									display: "flex",
									flexDirection: "column",
									position: "relative",
								}}
							>
								<div>
									{timesTitle && <h3 style={estiloH3NY}>{timesTitle}</h3>}
									{timesBodyHtml && (
										<div
											style={estiloPNY}
											dangerouslySetInnerHTML={{ __html: timesBodyHtml }}
										/>
									)}
								</div>
								{broadwaySrc && (
									<img
										src={broadwaySrc}
										alt="Broadway"
										style={{
											width: 100,
											position: "absolute",
											bottom: 0,
											left: isSmallScreen ? -80 : -80,
											zIndex: 2,
										}}
									/>
								)}
								{(broadwayBoxTitle || broadwayBoxBodyHtml) && (
									<div
										style={{
											backgroundColor: "#e0f7fa",
											border: "3px solid #59c3d8",
											borderRadius: 12,
											padding: "16px 20px",
											fontSize: 12,
											fontFamily: "'Poppins', sans-serif",
											color: "#333",
											marginTop: 8,
										}}
									>
										{broadwayBoxTitle && (
											<h4
												style={{
													margin: 0,
													marginBottom: 8,
													fontSize: 13,
													fontWeight: 700,
													display: "flex",
													alignItems: "center",
													gap: 6,
													color: "#007a8d",
													textTransform: "uppercase",
												}}
											>
												🎭 {broadwayBoxTitle}
											</h4>
										)}
										{broadwayBoxBodyHtml && (
											<p
												style={{
													fontFamily: "'Poppins', sans-serif",
													margin: 0,
												}}
											>
												<span
													dangerouslySetInnerHTML={{
														__html: broadwayBoxBodyHtml,
													}}
												/>
											</p>
										)}
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		</Box>
	);
};

export default Template73;
